import { useEffect, useState } from "react";
import { AlertTriangle, HardHat, Search } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { api, cx } from "@/lib/api";
import { fmtDate } from "@/lib/format";

const todayStr = () => new Date().toISOString().slice(0, 10);
const soonStr = () => new Date(Date.now() + 30 * 864e5).toISOString().slice(0, 10);

function statusOf(scadenza) {
  const t = todayStr(), s = soonStr();
  if (scadenza < t) return { key: "scaduta", label: "Scaduta", cls: "bg-red-100 text-red-700 dark:bg-red-950/40 dark:text-red-300" };
  if (scadenza <= s) return { key: "in_scadenza", label: "In scadenza", cls: "bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300" };
  return { key: "ok", label: "Valida", cls: "bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300" };
}

export default function Scadenze() {
  const [operai, setOperai] = useState([]);
  const [filter, setFilter] = useState("scadute");
  const [search, setSearch] = useState("");

  useEffect(() => { api.get("/operai").then(setOperai); }, []);

  const rows = [];
  operai.forEach((o) => {
    (o.certificazioni || []).forEach((c) => {
      if (c.scadenza) rows.push({ operaio: o.nome, nome: c.nome, scadenza: c.scadenza, ...statusOf(c.scadenza) });
    });
  });
  rows.sort((a, b) => a.scadenza.localeCompare(b.scadenza));

  const filtered = rows.filter((r) => {
    if (filter === "scadute" && r.key !== "scaduta") return false;
    if (filter === "in_scadenza" && r.key !== "in_scadenza") return false;
    if (search && !`${r.operaio} ${r.nome}`.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Scadenze Formazione</h1>
        <p className="text-sm text-muted-foreground mt-1">Certificazioni e corsi degli operai in scadenza o scaduti.</p>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <Tabs value={filter} onValueChange={setFilter}>
          <TabsList data-testid="scadenze-tabs">
            <TabsTrigger value="scadute" data-testid="tab-scadute">Scadute</TabsTrigger>
            <TabsTrigger value="in_scadenza" data-testid="tab-in-scadenza">In scadenza</TabsTrigger>
            <TabsTrigger value="tutte" data-testid="tab-tutte">Tutte</TabsTrigger>
          </TabsList>
        </Tabs>
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Cerca operaio o certificazione..." className="pl-9" data-testid="scadenze-search"
            value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          {filtered.length === 0 ? (
            <div className="py-16 text-center">
              <AlertTriangle className="mx-auto h-10 w-10 text-muted-foreground" />
              <p className="mt-4 font-medium">Nessuna scadenza in questa vista</p>
              <p className="text-sm text-muted-foreground">Aggiungi certificazioni agli operai dall'Archivio.</p>
            </div>
          ) : (
            <div className="divide-y divide-border">
              {filtered.map((r, i) => (
                <div key={i} className="flex items-center justify-between px-5 py-3" data-testid={`scadenza-row-${i}`}>
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-secondary text-amber-600"><HardHat className="h-4 w-4" /></div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{r.nome}</p>
                      <p className="text-xs text-muted-foreground truncate">{r.operaio}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <span className="text-sm text-muted-foreground">{fmtDate(r.scadenza)}</span>
                    <span className={cx("rounded-full px-2.5 py-0.5 text-[11px] font-medium", r.cls)}>{r.label}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
